import { useState, useEffect, createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

// Actions
import { restoreMapSavedState } from "../../../actions/map-view/SaveMapStateActions";
import { removeMediaSite } from "../../../actions/map-view/media-sites/MediaSitesActions";
import {
  closePriceSettingsCitiesForm,
  updateUserPriceSettings,
} from "../../../actions/org/OrgUserManageActions";

// Constants and Utils
import { useResetMapState } from "../../../mavin/utils/hooks/HooksUtil";
import {
  CampaignMapViewId,
  MapView,
} from "../../../constants/GeneralConstants";
import { MapInfoBox } from "../../map-view/boundaries/BoundariesUtil";
import { constructUserPriceEnabledCities } from "../../../common-utils/user-utils/userUtils";

// Components
import LLMap from "../../../components/map/leaflet-map/LLMap";
import TargetGroupHeatmap from "../../../components/map/target-group-heatmap/TargetGroupHeatmap";
import ConfirmationForm from "../../../components/confirmation-form/ConfirmationForm";
import MultiCitySelectionForm from "../../../mavin/components/multi-city-selection-form/MultiCitySelectionForm";

// Pages and Sections
import SiteMarkers from "../../map-view/media-sites/SiteMarkers";
import DataLayerMarkers from "../../map-view/data-layers/DataLayerMarkers";
import BoundaryMarkers from "../../map-view/boundaries/BoundaryMarkers";
import SecLayer from "../../map-view/sec/SecLayer";
import PoiMarkers from "../../../components/map/markers/PoiMarkers";
import RoadStretchMarkers from "../../map-view/road-stretches/RoadStretchMarkers";
import MapTabsSection from "../../map-view/MapTabsSection";
import MapControlsSection from "../../map-view/MapControlsSection";
import {
  MapViewLoader,
  CampaignMapViewHeader,
} from "./CampaignMapViewComponents";

// CSS
import "../../map-view/MapView.scss";

// context
export const orgMediaContext = createContext(null);

/**
 * Page
 */
function CampaignMapView() {
  const dispatch = useDispatch();
  const { id, cityId } = useParams();

  // State
  const [selectedTab, setSelectedTab] = useState(0);
  const [hoveredBoundaryInfo, setHoveredBoundaryInfo] = useState({});
  const [isOrgMedia, setIsOrgMedia] = useState(false);

  // Selector State
  const restoredCenter = useSelector(
    (state) => state.saveMapState.restoreCenter
  );
  const restoreLoading = useSelector(
    (state) => state.saveMapState.restoreLoading
  );
  const restoredZoom =
    useSelector((state) => state.saveMapState.restoreZoom) ||
    MapView.ZOOM_INDEX;
  const bbox = useSelector((state) => state.saveMapState.cityBBox);

  // Confirmation Form :: [true/false and dataObject]
  const openConfirmationForm = useSelector(
    (state) => state.confirmationModal.openModal
  );
  const processDataObject = useSelector(
    (state) => state.confirmationModal.processDataObject
  );

  // all "poiTypeLayer" points combined array to Show on Map after selecting any TG
  const allPoiPointsArr = useSelector((state) => state.geoData.allPoiPointsArr);
  // selected tgId (as key) to pass in "LLHeatLayer" component to re-render it
  const selectedTgId = useSelector(
    (state) => state.orgTargetGroup.selectedTgId
  );

  // Selector State
  const regionsName = useSelector((state) => state.region.regionsName) || [];

  // open Price Settings Modal"
  const openPriceSettingsModal = useSelector(
    (state) => state.orgUserManage.openPriceSettingsModal
  );

  // user priceEnabledCities
  const priceEnabledCityIds = useSelector(
    (state) => state.orgUserManage.priceEnabledCities
  );
  const priceEnabledCities = constructUserPriceEnabledCities(
    regionsName,
    priceEnabledCityIds
  );

  // Reset Map State
  useResetMapState();

  useEffect(() => {
    dispatch(restoreMapSavedState(id, cityId));
  }, [dispatch, id, cityId]);

  // Functions
  function tabOnClick(index) {
    if (selectedTab === index) {
      setSelectedTab(0);
      return;
    }
    setSelectedTab(index);
  }

  // This is passed in "ConfirmAction Form" to Remove selected media-site
  function removeMediaSiteFn() {
    dispatch(removeMediaSite(id, processDataObject.campaignMediaInfo));
  }

  // handle User Price Settings
  function handleUserPriceSettings(selectedCities) {
    const selectedCityIds = selectedCities.map((city) => city.id);
    dispatch(updateUserPriceSettings(selectedCityIds));

    // close form
    dispatch(closePriceSettingsCitiesForm());
  }

  return (
    <orgMediaContext.Provider value={[isOrgMedia, setIsOrgMedia]}>
      <div className="content-wrapper map-content" id={CampaignMapViewId}>
        {/* Header-Sections */}
        <CampaignMapViewHeader />

        {/* Loader and MapView */}
        {restoreLoading ? (
          <MapViewLoader />
        ) : (
          <>
            {/* Tabs Section */}
            <MapTabsSection
              selectedTab={selectedTab}
              tabOnClick={tabOnClick}
              campaignId={id}
              cityId={cityId}
              bbox={bbox}
            />

            {/* Map Container */}
            {restoredCenter && (
              <div className="map-cont">
                <LLMap
                  center={[
                    Number(restoredCenter[0]),
                    Number(restoredCenter[1]),
                  ]}
                  zoom={restoredZoom}
                >
                  {/* CampaignMedia and InventoryMedia Markers */}
                  <SiteMarkers isOrgMedia={isOrgMedia} />

                  <PoiMarkers />
                  <DataLayerMarkers cityId={cityId} />

                  {/* This component covers pinCodes and Wards Layers */}
                  <BoundaryMarkers
                    setHoveredBoundaryInfo={setHoveredBoundaryInfo}
                  />

                  <SecLayer />

                  {/* Target-Group-HeatMap */}
                  <TargetGroupHeatmap
                    hmkey={selectedTgId}
                    allPoiPointsArr={allPoiPointsArr}
                  />
                  <RoadStretchMarkers />

                  {/* Map Info Box */}
                  <MapInfoBox hoveredBoundaryInfo={hoveredBoundaryInfo} />
                </LLMap>
              </div>
            )}

            {/* Legends for mapView */}
            <MapControlsSection />
          </>
        )}

        {/** Modals */}
        {openConfirmationForm && (
          <ConfirmationForm onClick={removeMediaSiteFn} />
        )}

        {/* for Patrika */}
        {openPriceSettingsModal && (
          <MultiCitySelectionForm
            regionsName={regionsName}
            onSubmit={handleUserPriceSettings}
            citiesList={priceEnabledCities}
            callBackCloseForm={() => dispatch(closePriceSettingsCitiesForm())}
          />
        )}
      </div>
    </orgMediaContext.Provider>
  );
}

export default CampaignMapView;
